import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { TimeRange } from "@/components/TimeRangeSelector";

export function useSavingsBreakdown(tenantId: string, range: TimeRange, userId = "") {
  return useQuery({
    queryKey: ["savings-breakdown", tenantId, range.from, range.to, userId],
    queryFn: async () => {
      const searchParams: Record<string, string> = { from: range.from, to: range.to };
      if (userId) searchParams.user_id = userId;
      const json = await api
        .get("savings/breakdown", { searchParams })
        .json<Record<string, string>>();
      return json;
    },
    enabled: !!tenantId,
  });
}

export function useBillingCurrent(tenantId: string) {
  return useQuery({
    queryKey: ["billing-current", tenantId],
    queryFn: async () => {
      const json = await api.get("billing/current").json<{
        period_start: string;
        period_end: string;
        status: string;
        total_verified_savings: string;
        total_baseline_cost: string;
        total_actual_cost: string;
        billable_requests: number;
        projected_invoice?: string;
        total_invoice_amount?: string;
        savings_fee?: string;
        savings_fee_rate?: string;
      }>();
      return json;
    },
    enabled: !!tenantId,
  });
}

export function useBillingHistory(tenantId: string) {
  return useQuery({
    queryKey: ["billing-history", tenantId],
    queryFn: async () => {
      const json = await api.get("billing/history").json<
        Array<{
          id: string;
          period_start: string;
          period_end: string;
          status: string;
          total_baseline_cost: string;
          total_actual_cost: string;
          total_verified_savings: string;
          total_invoice_amount: string;
        }>
      >();
      return json;
    },
    enabled: !!tenantId,
  });
}

export function useSavingsEventByRequest(requestId: string | null) {
  return useQuery({
    queryKey: ["savings-event", requestId],
    queryFn: async () => {
      const json = await api
        .get(`savings/event-by-request/${requestId}`)
        .json<{ savings_event: Record<string, unknown> | null }>();
      return json.savings_event;
    },
    enabled: !!requestId,
  });
}
